import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { agentsAPI, publishingAPI, mediaAPI, getAuthenticatedMediaUrl } from '../services/api';
import { FiMonitor, FiImage, FiVideo, FiFile, FiWifi, FiWifiOff } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const DeviceMedia = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentMediaList, setAgentMediaList] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, [user?.compID]);

  const fetchAgents = async () => {
    if (!user?.compID) return;
    
    try {
      setLoading(true);
      const agentsData = await agentsAPI.getAgents(user.compID);
      // Only show active agents
      setAgents(agentsData.filter(agent => agent.agentActive));
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentMedia = async (agent) => {
    setSelectedAgent(agent);
    setLoadingMedia(true);
    setAgentMediaList([]);

    try {
      // Get the agent's publishing assignments to know what media group is assigned
      const assignments = await publishingAPI.getAgentAssignments(user.compID, agent.agentID);
      
      if (assignments && assignments.length > 0) {
        // Get the latest assignment
        const latestAssignment = assignments[0];
        
        // Fetch all media from the company
        const allMedia = await mediaAPI.getAllMedia(user.compID);
        
        // Filter media by the assigned media group
        const assignedMedia = allMedia.filter(
          media => media.mediaGroupID === latestAssignment.mediaGroupID
        );
        
        setAgentMediaList(assignedMedia);
      } else {
        // No assignments found
        setAgentMediaList([]);
        toast.info('No media published to this device yet');
      }
    } catch (error) {
      console.error('Error fetching agent media:', error);
      toast.error('Failed to load device media');
      setAgentMediaList([]);
    } finally {
      setLoadingMedia(false);
    }
  };

  const isAgentOnline = (lastOnline) => {
    if (!lastOnline) return false;
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return new Date(lastOnline) > fiveMinutesAgo;
  };

  const getMediaIcon = (mediaType) => {
    if (mediaType?.startsWith('image')) return <FiImage className="w-6 h-6" />;
    if (mediaType?.startsWith('video')) return <FiVideo className="w-6 h-6" />;
    return <FiFile className="w-6 h-6" />;
  };

  const getMediaPreviewUrl = (mediaUrl) => {
    const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5001';
    
    if (!mediaUrl) return null;
    return getAuthenticatedMediaUrl(mediaUrl);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Device Media</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Devices List Section */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Devices</h2>
              <p className="text-sm text-gray-500 mt-1">
                Click a device to view its published media
              </p>
            </div>
            
            <div className="divide-y divide-gray-200">
              {loading ? (
                <div className="p-6 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-2">Loading devices...</p>
                </div>
              ) : agents.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <FiMonitor className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No active devices found</p>
                </div>
              ) : (
                agents.map((agent) => (
                  <button
                    key={agent.agentID}
                    onClick={() => fetchAgentMedia(agent)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedAgent?.agentID === agent.agentID ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <FiMonitor className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {agent.agentName}
                          </h3>
                          {agent.agentPosition && (
                            <p className="text-xs text-gray-500 truncate">
                              {agent.agentPosition}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {agent.agentOrientation || 'landscape'}
                          </p>
                        </div>
                      </div>
                      <div className="ml-2">
                        {isAgentOnline(agent.lastOnline) ? (
                          <FiWifi className="w-4 h-4 text-green-500" title="Online" />
                        ) : (
                          <FiWifiOff className="w-4 h-4 text-gray-400" title="Offline" />
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Media Preview Section */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedAgent ? `Media on ${selectedAgent.agentName}` : 'Select a Device'}
              </h2>
              {selectedAgent && (
                <p className="text-sm text-gray-500 mt-1">
                  Last published media content
                </p>
              )}
            </div>
            
            <div className="p-6">
              {!selectedAgent ? (
                <div className="text-center py-12">
                  <FiMonitor className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">Select a device from the list to view its published media</p>
                </div>
              ) : loadingMedia ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-4 text-gray-500">Loading media...</p>
                </div>
              ) : agentMediaList.length === 0 ? (
                <div className="text-center py-12">
                  <FiImage className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No media published to this device</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {agentMediaList.map((media) => (
                    <div
                      key={media.mediaID}
                      className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow"
                    >
                      {/* Media Preview */}
                      <div className="aspect-video bg-gray-900 relative">
                        {media.mediaType?.startsWith('image') ? (
                          <img
                            src={getMediaPreviewUrl(media.mediaUrl)}
                            alt={media.mediaName}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Preview%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        ) : media.mediaType?.startsWith('video') ? (
                          <video
                            src={getMediaPreviewUrl(media.mediaUrl)}
                            className="w-full h-full object-contain"
                            controls
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            {getMediaIcon(media.mediaType)}
                            <span className="ml-2 text-white text-sm">Preview not available</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Media Info */}
                      <div className="p-3">
                        <h3 className="text-sm font-medium text-gray-900 truncate" title={media.mediaName}>
                          {media.mediaName}
                        </h3>
                        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                          <span className="flex items-center">
                            {getMediaIcon(media.mediaType)}
                            <span className="ml-1">{media.mediaType?.split('/')[1]?.toUpperCase() || 'FILE'}</span>
                          </span>
                          {media.mediaDuration && (
                            <span>{media.mediaDuration}s</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceMedia;
